import styled from 'styled-components';
import React, { FC, FormEventHandler, useEffect, useState } from 'react';
import theme from '../utils/theme';
import PrimaryButton from './Button';
import { SecondaryButton } from './Button';

type LookupEntry = {
  datasetId: string;
  lookupKey: string;
  lookupKeyType: string;
};

const PAGE_SIZE = 20;

export const SocialPixelLookupForm: FC<{ authToken: string }> = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [entries, setEntries] = useState<LookupEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const orderedEntries = [...entries].reverse();
  const totalPages = Math.max(1, Math.ceil(orderedEntries.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageEntries = orderedEntries.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const authHeaders = { Authorization: 'Bearer ' + props.authToken };

  const fetchEntries = () => {
    fetch(`${process.env.PRIVATE_API_URL}/social-pixel-lookups`, {
      headers: authHeaders,
      mode: 'cors',
    }).then((response) => {
      if (response.status !== 200) {
        response.text().then((body) => setError(body.trim().length > 0 ? body : 'Failed to fetch entries'));
      } else {
        response.json().then((data) => setEntries(data));
      }
    });
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  const handleFormSubmission: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();

    const form = e.currentTarget;
    const payload = new URLSearchParams(new FormData(form) as any);

    setIsLoading(true);
    setError(null);
    setResult(null);

    fetch(form.action, {
      method: 'post',
      body: payload,
      headers: authHeaders,
    })
      .then((response) => {
        setIsLoading(false);
        if (response.status !== 200) {
          response.text().then((body) => setError(body.trim().length > 0 ? body : 'Failed to create entry'));
        } else {
          setResult('Entry created successfully');
          response.json().then((data) => setEntries(data));
          form.reset();
        }
      })
      .catch((e) => {
        setIsLoading(false);
        setError(e instanceof Error ? e.message : e.toString());
      });
  };

  const handleDelete = (lookupKey: string) => {
    if (!confirm(`Delete entry with lookup key "${lookupKey}"?`)) return;

    setDeletingKey(lookupKey);
    setError(null);
    setResult(null);

    fetch(`${process.env.PRIVATE_API_URL}/social-pixel-lookups/${encodeURIComponent(lookupKey)}`, {
      method: 'delete',
      headers: authHeaders,
      mode: 'cors',
    })
      .then((response) => {
        setDeletingKey(null);
        if (response.status !== 200) {
          response.text().then((body) => setError(body.trim().length > 0 ? body : 'Failed to delete entry'));
        } else {
          setResult('Entry deleted successfully');
          response.json().then((data) => setEntries(data));
        }
      })
      .catch((e) => {
        setDeletingKey(null);
        setError(e instanceof Error ? e.message : e.toString());
      });
  };

  return (
    <>
      <h3>Social Pixel Lookups</h3>

      <form method="POST" action={`${process.env.PRIVATE_API_URL}/social-pixel-lookups`} onSubmit={handleFormSubmission}>
        <p>
          <Label htmlFor="dataset-id">Dataset ID:</Label>
          <Input type="text" name="dataset-id" required={true} placeholder="e.g. 1402242201656558" />
        </p>
        <p>
          <Label htmlFor="lookup-key">Lookup Key:</Label>
          <Input type="text" name="lookup-key" required={true} placeholder="e.g. f1f1d25e601e4cafb9c8ec0b5cc743db" />
        </p>
        <p>
          <Label htmlFor="lookup-key-type">Lookup Key Type:</Label>
          <select name="lookup-key-type" required={true}>
            <option value="API_KEY">API_KEY</option>
            <option value="CLIENT_KEY">CLIENT_KEY</option>
          </select>
        </p>
        <PrimaryButton disabled={isLoading} isLoading={isLoading} type="submit">
          Add Entry
        </PrimaryButton>

        {result !== null && <p style={{ color: theme.colours.confirmation400 }}>{result}</p>}
        {error !== null && <p style={{ color: theme.colours.alert400 }}>{error}</p>}
      </form>

      {entries.length > 0 && (
        <Table>
          <thead>
            <tr>
              <Th>Dataset ID</Th>
              <Th>Lookup Key</Th>
              <Th>Key Type</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {pageEntries.map((entry) => (
              <tr key={entry.lookupKey}>
                <Td>{entry.datasetId}</Td>
                <Td>
                  {entry.lookupKeyType === 'API_KEY' && (
                    <a
                      href={`https://slipway.lh.triptease.io/entity/apiKey:${entry.lookupKey}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {entry.lookupKey}
                    </a>
                  )}
                  {entry.lookupKeyType === 'CLIENT_KEY' && (
                    <a
                      href={`https://slipway.lh.triptease.io/entity/clientKey:${entry.lookupKey}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {entry.lookupKey}
                    </a>
                  )}
                  {entry.lookupKeyType !== 'API_KEY' && entry.lookupKeyType !== 'CLIENT_KEY' && entry.lookupKey}
                </Td>
                <Td>{entry.lookupKeyType}</Td>
                <Td>
                  <SecondaryButton
                    disabled={deletingKey === entry.lookupKey}
                    isLoading={deletingKey === entry.lookupKey}
                    onClick={() => handleDelete(entry.lookupKey)}
                  >
                    Delete
                  </SecondaryButton>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {entries.length > PAGE_SIZE && (
        <Pagination>
          <SecondaryButton disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}>
            Previous
          </SecondaryButton>
          <PageInfo>
            Page {currentPage} of {totalPages}
          </PageInfo>
          <SecondaryButton disabled={currentPage >= totalPages} onClick={() => setPage(currentPage + 1)}>
            Next
          </SecondaryButton>
        </Pagination>
      )}
    </>
  );
};

const Label = styled.label`
  font-size: 14px;
  display: block;
  margin-bottom: 5px;
  font-weight: ${theme.fontWeightMedium};
  min-width: 200px;
`;

const Input = styled.input`
  height: 44px;
  color: ${theme.colours.black};
  top: 25px;
  left: 12px;
  line-height: 20px;
  padding-left: 12px;
  width: 350px;
`;

const Table = styled.table`
  margin-top: 20px;
  border-collapse: collapse;
  width: 100%;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px 12px;
  border-bottom: 2px solid ${theme.colours.gray300};
  font-size: 14px;
  font-weight: ${theme.fontWeightMedium};
`;

const Td = styled.td`
  padding: 8px 12px;
  border-bottom: 1px solid ${theme.colours.gray300};
  font-size: 14px;
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
`;

const PageInfo = styled.span`
  font-size: 14px;
`;